import React, { useEffect, useState } from 'react';
import { ThemeProvider, CssBaseline, Container, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import TrainIcon from '@mui/icons-material/Train';
import StraightenIcon from '@mui/icons-material/Straighten';
import FunctionsIcon from '@mui/icons-material/Functions';
import AirlineSeatLegroomExtraIcon from '@mui/icons-material/AirlineSeatLegroomExtra';
import { Vuoro, Juna } from './Kirjaa';

interface Props {
  kayttajaId: number;
  token: string;
}




const theme = createTheme({
  palette: {
    mode: 'dark',
  },
});

const EARTH_CIRCUMFERENCE_KM = 40075;

const Tilastot: React.FC<Props> = (props: Props): React.ReactElement => {
  const [userShifts, setUserShifts] = useState<Vuoro[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUserShifts = async () => {
    try {

      const response = await fetch(`/api/ajot?userid=${props.kayttajaId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${props.token}`
        }
      });

      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }



      const data: Vuoro[] = await response.json();
      let filteredShifts = data.filter(vuoro => vuoro.userid === props.kayttajaId);

      filteredShifts = filteredShifts.map(vuoro => ({
        ...vuoro,
        junat: vuoro.junat.filter(juna => !juna.matkustus)
      }));

      setUserShifts(filteredShifts);
      setLoading(false);
    } catch (error: any) {
      setError(error.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loading) {
      fetchUserShifts();
    }
  }, [props.kayttajaId, props.token]);

  const calculateTotalKilometers = () => {
    return userShifts.reduce((total, vuoro) => {
      return total + vuoro.junat.reduce((sum, juna) => sum + (juna.kmM || 0) + (juna.kmT || 0), 0);
    }, 0);
  };

  const calculateHours = () => {
    const totalMinutes = userShifts.reduce((total, vuoro) => total + (vuoro.tyoaika || 0), 0);
    return Math.floor(totalMinutes / 60);
  };

  const calculateNightHours = () => {
    const totalNightMinutes = userShifts.reduce((total, vuoro) => total + (vuoro.yotunnit || 0), 0);
    return Math.floor(totalNightMinutes / 60);
  };

  const calculateFavoriteLocomotive = () => {
    const locomotiveCount: { [key: string]: number } = {};
    userShifts.forEach(vuoro => {
      vuoro.junat.forEach(juna => {
        locomotiveCount[juna.veturi] = (locomotiveCount[juna.veturi] || 0) + 1;
      });
    });
    return Object.keys(locomotiveCount).reduce((a, b) => locomotiveCount[a] > locomotiveCount[b] ? a : b, '');
  };

  const calculateHeaviestTrain = () => {
    let heaviestTrainWeight = 0;
    let heaviestTrainNumber = 0;
    userShifts.forEach(vuoro => {
      vuoro.junat.forEach(juna => {
        if (juna.junapaino > heaviestTrainWeight) {
          heaviestTrainWeight = juna.junapaino;
          heaviestTrainNumber = juna.junanumero;
        }
      });
    });
    return `${heaviestTrainWeight} tn (juna ${heaviestTrainNumber})`;
  };

  const calculateTimesAroundTheWorld = () => {
    const totalKilometers = calculateTotalKilometers();
    return (totalKilometers / EARTH_CIRCUMFERENCE_KM).toFixed(2);
  };



  const calculateMostTraveledRoute = () => {
    const routeDistances: { [key: string]: number } = {};
    userShifts.forEach(vuoro => {
      vuoro.junat.forEach(juna => {
        const route = `${juna.mista} - ${juna.mihin}`;
        const distance = (juna.kmM || 0) + (juna.kmT || 0);
        routeDistances[route] = (routeDistances[route] || 0) + distance;
      });
    });
    return Object.keys(routeDistances).reduce((a, b) => routeDistances[a] > routeDistances[b] ? a : b, '');
  };

  const calculateAverageTrainLength = () => {
    const totalLength = userShifts.reduce((total, vuoro) => {
      return total + vuoro.junat.reduce((sum, juna) => sum + (juna.pituus || 0), 0);
    }, 0);
    const totalTrains = userShifts.reduce((count, vuoro) => count + vuoro.junat.length, 0);
    return (totalLength / totalTrains).toFixed(2);
  };

  const calculateLongestTrain = () => {
    let longestTrainLength = 0;
    let longestTrainNumber = 0;
    userShifts.forEach(vuoro => {
      vuoro.junat.forEach(juna => {
        if (juna.pituus > longestTrainLength) {
          longestTrainLength = juna.pituus;
          longestTrainNumber = juna.junanumero;
        }
      });
    });
    return `${longestTrainLength} m (juna ${longestTrainNumber})`;
  };

  const calculateSafetyDevicePressed = () => {
    const totalSafetyDevicePresses = userShifts.reduce((total, vuoro) => {
      return total + vuoro.junat.reduce((sum, juna) => {
        const lahtoaika = new Date(juna.lahtoaika).getTime();
        const saapumisaika = new Date(juna.saapumisaika).getTime();
        const durationInMinutes = (saapumisaika - lahtoaika) / (1000 * 60) * 2.17;

        return sum + Math.max(0, durationInMinutes);
      }, 0);
    }, 0);

    return Math.floor(totalSafetyDevicePresses);
  };

  const calculateTotalTrainHours = () => {
    const totalTrainMinutes = userShifts.reduce((total, vuoro) => {
      return total + vuoro.junat.reduce((sum, juna) => {
        const lahtoaika = new Date(juna.lahtoaika).getTime();
        const saapumisaika = new Date(juna.saapumisaika).getTime();
        const duration = (saapumisaika - lahtoaika) / (1000 * 60);
        return sum + duration;
      }, 0);
    }, 0);

    const hours = Math.floor(totalTrainMinutes / 60);
    const minutes = Math.floor(totalTrainMinutes % 60);
    return `${hours} h ${minutes} min`;
  };

  if (loading) {
    return <Typography variant="h6">Loading...</Typography>;
  }

  if (error) {
    return <Typography variant="h6" color="error">{error}</Typography>;
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Container maxWidth="md">
        <Paper sx={{ padding: 2, marginBottom: 2 }}>
          <Typography variant="h4" gutterBottom>
            Tilastoja
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  {/* Table headers can be added here if needed */}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell><TrainIcon /></TableCell>
                  <TableCell>Kilometrit</TableCell>
                  <TableCell>{calculateTotalKilometers()} km ({calculateTimesAroundTheWorld()} kertaa maapallon ympäri)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><AccessTimeIcon /></TableCell>
                  <TableCell>Tunnit</TableCell>
                  <TableCell>{calculateHours()} h</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><NightsStayIcon /></TableCell>
                  <TableCell>Yötunnit</TableCell>
                  <TableCell>{calculateNightHours()} h</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><TrainIcon /></TableCell>
                  <TableCell>Eniten ajettu reitti</TableCell>
                  <TableCell>{calculateMostTraveledRoute()}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><FavoriteIcon /></TableCell>
                  <TableCell>Suosikkiveturi</TableCell>
                  <TableCell>{calculateFavoriteLocomotive()}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><FitnessCenterIcon /></TableCell>
                  <TableCell>Painavin juna</TableCell>
                  <TableCell>{calculateHeaviestTrain()}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><StraightenIcon /></TableCell>
                  <TableCell>Pisin juna</TableCell>

                  <TableCell>{calculateLongestTrain()}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><StraightenIcon /></TableCell>
                  <TableCell>Keskimääräinen junan pituus</TableCell>
                  <TableCell>{calculateAverageTrainLength()} m</TableCell>
                </TableRow>



                <TableRow>
                  <TableCell><FunctionsIcon /></TableCell>
                  <TableCell>Ajoaika yhteensä</TableCell>
                  <TableCell>{calculateTotalTrainHours()}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><AirlineSeatLegroomExtraIcon /></TableCell>
                  <TableCell>Turvalaitetta painettu</TableCell>
                  <TableCell>{calculateSafetyDevicePressed()} kertaa</TableCell>
                </TableRow>


              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default Tilastot;
